import "../../App.css";
import "../../Events.css";
import React, { useRef, useState, useEffect } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import NavMenu from "./components/NavMenu";
import HomePanel from "./components/HomePanel";
import { toggleAllVideos } from "../../utils/Actions/toggleAllVideos";
import { useScrollSnap } from "../../utils/Gsap/useScrollSnap";
import { initScrollControl } from "../../utils/Gsap/gsapHelpers";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Helmet } from "react-helmet";
import { getLandingPageData } from "../../utils/api/helpers/pages";
import {
  useAllVideoPaused,
  setAllVideoPaused,
} from "../../utils/Home/homeStates";
import PageLoader from "../../utils/Ui/PageLoader";
import FooterHome from "../../components/Footer/FooterHome";

export default function Home() {
  gsap.registerPlugin(ScrollTrigger);

  const allVideoPaused = useAllVideoPaused();

  const [isLoading, setIsLoading] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [activeSection, setActiveSection] = useState("");
  const container = useRef();
  const indicatorRef = useRef();
  const navRefs = useRef({});
  const allVideoPausedRef = useRef(allVideoPaused);
  const submenuTimelines = useRef({});

  const programmaticScroll = useScrollSnap();

  useEffect(() => {
    allVideoPausedRef.current = allVideoPaused;
  }, [allVideoPaused]);

  useEffect(() => {
    gsap.ticker.lagSmoothing(1000, 33); // global setting
  }, []);

  useEffect(() => {
    getLandingPageData()
      .then((result) => {
        // console.log("RES HOME", result.data.GenreList);
        setHomeData(result.data.GenreList);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Initialize GSAP scroll and nav
  useGSAP(
    () => {
      initScrollControl({
        containerRef: container,
        navRefs,
        indicatorRef,
        submenuTimelines,
        allVideoPausedRef,
        setActiveSection,
      });
    },
    { scope: container, dependencies: [homeData] },
  );

  // The nav and the play/pause button are fixed, so the footer would scroll in
  // underneath them. Lift them by the footer's height as it comes into view, so
  // the footer ends up with the viewport to itself.
  useGSAP(
    () => {
      if (!homeData) return;

      const footer = document.querySelector(".footer-home");
      const nav = document.querySelector(".nav");
      const playPause = document.querySelector(".title-playpause");

      if (!footer || !nav) return;

      const lift = () => -footer.offsetHeight;
      const scrollTrigger = {
        trigger: footer,
        start: "top bottom",
        end: "bottom bottom",
        scrub: true,
        invalidateOnRefresh: true,
      };

      // .nav is centred in CSS with translateY(-50%). Animating y alone would
      // replace that transform and drop the nav down the screen, so GSAP owns
      // the centring here via yPercent.
      gsap.fromTo(
        nav,
        { yPercent: -50, y: 0 },
        { yPercent: -50, y: lift, ease: "none", scrollTrigger },
      );

      if (playPause) {
        gsap.fromTo(
          playPause,
          { y: 0 },
          { y: lift, ease: "none", scrollTrigger: { ...scrollTrigger } },
        );
      }
    },
    { dependencies: [homeData] },
  );

  return (
    <>
      <PageLoader isLoading={isLoading} />
      <div ref={container} id="container">
        <Helmet>
          <title>Netflix Series FYC</title>
          <meta name="description" content="Netflix Series FYC" />
        </Helmet>

        <NavMenu
          navData={homeData}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          navRefs={navRefs}
          indicatorRef={indicatorRef}
          submenuTimelines={submenuTimelines}
          programmaticScroll={programmaticScroll}
        />

        <main>
          {homeData &&
            homeData.map((item, i) => (
              <HomePanel key={item.slug} item={item} panelIndex={i} />
            ))}

          <button
            className="title-playpause"
            onClick={() => {
              toggleAllVideos(!allVideoPaused);
              setAllVideoPaused(!allVideoPaused);
            }}
          >
            <div className="title-playpause-button">
              <img
                src={
                  allVideoPaused ? "/img/ico/play.png" : "/img/ico/pause.png"
                }
                alt={allVideoPaused ? "Play all videos" : "Pause all videos"}
                className="title-playpause-icon"
              />
            </div>
            <div className="title-playpause-label">
              {allVideoPaused ? "Play" : "Pause"} All Video
            </div>
          </button>
        </main>

        {/* Sits below the panels. Panel ScrollTriggers are all relative to each
            panel's own box, so this doesn't affect them; scroll snapping stops
            once the last panel is behind you (see useScrollSnap). */}
        {homeData && <FooterHome />}
      </div>
    </>
  );
}
